import {
	type BulkAclEntity,
	type ReferenceUniverseListEntry,
	InvestmentExportConverterType,
} from "@eu.mdotm.api/sphere";
import { runWithErrorReporting } from "$root/api/error-reporting";
import { hasAccess } from "$root/components/AuthorizationGuard";
import { spawnYesNoDialog } from "$root/components/spawnable/yes-no-dialog";
import { roleByArea } from "$root/functional-areas/acl/checkers/all";
import { validateACLPermissions } from "$root/functional-areas/acl/checkers/shared";
import { useUpdateAccessListControlDialog } from "$root/functional-areas/acl/hook/useUpdateAccessListControlDialog";
import {
	checkBulkActionAvailability,
	BulkActionReviewSections,
	spawnBulkActionReviewDialog,
} from "$root/functional-areas/portfolio-studio/bulk-actions/common";
import { UniverseActionUnavailableReason } from "$root/functional-areas/portfolio-studio/bulk-actions/universe-actions";
import { universeListActionValidators } from "$root/functional-areas/portfolio-studio/bulk-actions/universe-actions/validators";
import { universeReasonToMessageMap } from "$root/functional-areas/portfolio-studio/bulk-actions/universe-actions/reason-messages";
import { spawnBulkExportAction } from "$root/functional-areas/portfolio-studio/bulk-actions/spawn-bulk-export-action";
import { spawnBulkShareAction } from "$root/functional-areas/portfolio-studio/bulk-actions/spawn-bulk-share-action";
import { universeEntityManagementActions } from "$root/functional-areas/universe/entity-management";
import { universeRepository } from "$root/functional-areas/universe/repository";
import { universeWithGroupsRepository } from "$root/functional-areas/universe/universeWithGroupsRepository";
import { useUserValue } from "$root/functional-areas/user";
import { persistedColStateKeys } from "$root/functional-areas/user/userStorage";
import { AgGridWithModules, useAgGridSelectedNodes } from "$root/third-party-integrations/ag-grid/ag-grid";
import { columnsDefinition } from "$root/ui-lib/table/column";
import { agExtra } from "$root/ui-lib/table/default-configuration";
import { parallelize, preventSubmitOnPressEnter, ToastableError } from "$root/utils";
import { Icon } from "@mdotm/shadcn/legacy/components";
import { TextInput, BulkActions, type BulkActionDropdownItem } from "@mdotm/shadcn/mdotm/components";
import { useDebouncedMemo } from "@mdotm/shadcn/legacy/react-extensions";
import { noop, unpromisify } from "@mdotm/shadcn/legacy/utils";
import type { GetContextMenuItems } from "ag-grid-community";
import type { AgGridReact } from "ag-grid-react";
import { type FC, useCallback, useMemo, useState } from "react";
import { renderToStaticMarkup } from "react-dom/server";
import { useTranslation } from "react-i18next";
import { multipleEntityDelete, notifyUser } from "../common";

type UniverseListProps = {
	universes: ReferenceUniverseListEntry[];
};

const UniverseList: FC<UniverseListProps> = ({ universes }) => {
	const [searchQuery, setSearchQuery] = useState("");
	const [gridEl, setGridEl] = useState<AgGridReact<ReferenceUniverseListEntry> | null>(null);
	const selectedNodes = useAgGridSelectedNodes(gridEl);

	const { t } = useTranslation();

	const user = useUserValue();
	const { aclSpawn: aclSpawnFlat } = useUpdateAccessListControlDialog("UNIVERSE");
	const { aclSpawn: aclSpawnGroup } = useUpdateAccessListControlDialog("GROUP_UNIVERSE");

	const { value: debouncedLowerCaseSearchQuery } = useDebouncedMemo(
		() => searchQuery.toLocaleLowerCase(),
		[searchQuery],
		{
			debounceInterval: 200,
		},
	);

	const filteredRows = useMemo(
		() =>
			universes.filter((item) =>
				[item.name ?? "", item.status ?? ""].some((el) => el.toLowerCase().includes(debouncedLowerCaseSearchQuery)),
			),
		[debouncedLowerCaseSearchQuery, universes],
	);

	const getContextMenuItems = useCallback<GetContextMenuItems<ReferenceUniverseListEntry, any>>(
		(row) => {
			const universe = row.node?.data;
			if (!universe) {
				return ["copy"];
			}
			const entityManagementActions = universeEntityManagementActions(
				user,
				universe,
				undefined,
				universe.universeType === "GROUP_UNIVERSE" ? universeWithGroupsRepository : universeRepository,
			);
			return [
				{
					icon: renderToStaticMarkup(<Icon icon="Delete" size={16} />),
					disabled: !entityManagementActions.deleteAsync,
					action: unpromisify(entityManagementActions.deleteAsync ?? noop),
					name: "Delete",
				},
				{
					icon: renderToStaticMarkup(<Icon icon="Content-Copy" size={16} />),
					disabled: !entityManagementActions.duplicateAsync,
					action: unpromisify(entityManagementActions.duplicateAsync ?? noop),
					name: "Duplicate",
				},
				{
					icon: renderToStaticMarkup(<Icon icon="Edit" size={16} />),
					disabled: !entityManagementActions.renameAsync,
					action: unpromisify(entityManagementActions.renameAsync ?? noop),
					name: "Rename",
				},
				{
					icon: renderToStaticMarkup(<Icon icon="share" size={16} />),
					action: unpromisify(() =>
						universe.universeType === "GROUP_UNIVERSE"
							? aclSpawnGroup(universe.name, universe.uuid)
							: aclSpawnFlat(universe.name, universe.uuid),
					),
					name: "Share",
					disabled: !validateACLPermissions(user.id, universe.richAcl?.acl ?? [], roleByArea.universe.EDITOR),
				},
			];
		},
		[user, aclSpawnGroup, aclSpawnFlat],
	);

	const columns = columnsDefinition.useUniverseList();

	return (
		<>
			<div className="min-h-0 min-w-0 flex flex-1 flex-col basis-0! rounded-lg bg-white px-4 py-6">
				<TextInput
					value={searchQuery}
					name="search"
					maxLength={60}
					onChangeText={setSearchQuery}
					placeholder={t("PORTFOLIOS.CONSTRAINTS_TARGETS.PORTFOLIO_LIST")}
					onKeyDown={preventSubmitOnPressEnter}
					style={{ width: 692 }}
					leftContent={<Icon icon="Search" size="1.4em" />}
					className="mb-4"
				/>

				<AgGridWithModules<ReferenceUniverseListEntry>
					{...agExtra.checkboxColumn({
						mode: "multiRow",
						selectAll: "currentPage",
					})}
					{...agExtra.usePersistColState(persistedColStateKeys["tableV3-universeList"])}
					innerRef={setGridEl}
					getContextMenuItems={getContextMenuItems}
					columnDefs={columns}
					rowData={filteredRows}
				/>
			</div>

			<BulkActions
				variant="floating"
				count={selectedNodes.length}
				onClear={() => gridEl?.api.deselectAll()}
				actions={[
					{
						label: "Delete",
						icon: "Delete",
						onClick() {
							const reasonKeys = Object.values(UniverseActionUnavailableReason);
							const availabilityResult = checkBulkActionAvailability(
								reasonKeys,
								universeListActionValidators.delete,
								selectedNodes,
							);
							const [available, unavailable] = availabilityResult;

							async function onContinue() {
								const response = await multipleEntityDelete({ area: "Universes", items: available });
								gridEl?.api.deselectAll();
								notifyUser({ t, succeded: response.succeded, area: "Universes" });
							}

							if (unavailable === null) {
								spawnYesNoDialog({
									size: "large",
									header: t("PORTFOLIOS.MODAL.BULK_DELETE.TITLE"),
									yesButton: t("BUTTON.DELETE"),
									noButton: t("BUTTON.CANCEL"),
									children: t("PORTFOLIOS.MODAL.BULK_DELETE.DESCRIPTION", {
										size: available.length,
									}),
									onSubmitAsync: onContinue,
								})
									.then(noop)
									.catch(noop);
							} else {
								spawnBulkActionReviewDialog({
									initialState: null,
									header: "Delete: review and confirm",
									children: (
										<BulkActionReviewSections
											availabilityResult={availabilityResult}
											reasonToMessage={universeReasonToMessageMap}
											getRedirectUrl={() => ""}
											hideRedirectUrl
											showEntityNames
											labels={{ entitySingular: "Universe", entityPlural: "Universes" }}
										/>
									),
									canConfirm: available.length > 0,
									onContinue,
								}).catch(noop);
							}
						},
						"data-qualifier": "UniverseList/BatchAction(Delete)",
					},
					{
						label: "Download",
						icon: "Dowload",
						dropdownItems: [
							{
								icon: "xls",
								onClickAsync: async () => {
									const callStack = selectedNodes.map(
										(universe) => () =>
											runWithErrorReporting(
												async () => {
													const uuid = universe?.uuid;
													if (!uuid) {
														throw new ToastableError("Something went wrong", { icon: "Portfolio" });
													}

													await universeRepository.downloadUniverse(uuid);
												},
												{
													area: "universe",
													attemptedOperation: { message: "try to download universe template" },
												},
											),
									);
									await parallelize(callStack, { concurrency: 3 });
								},
								label: "Universe template",
								"data-qualifier": "UniverseList/DropdownItem(UniverseTemplate)",
							},
							...(hasAccess(user, { requiredService: "EXPORT" }) &&
							(user.exportFormats ?? []).includes(InvestmentExportConverterType.SphereBulkTemplateConverter)
								? [
										{
											label: `${t(`EXPORT.${InvestmentExportConverterType.SphereBulkTemplateConverter}`)} template`,
											icon: "xls",
											onClickAsync: () =>
												spawnBulkExportAction({
													reasonKeys: Object.values(UniverseActionUnavailableReason),
													validator: universeListActionValidators.export,
													entities: selectedNodes,
													reasonToMessage: universeReasonToMessageMap,
													labels: { entitySingular: "Universe", entityPlural: "Universes" },
													getRedirectUrl: () => "",
													hideRedirectUrl: true,
													showEntityNames: true,
													area: "universe",
													downloadBulkTemplate: universeRepository.downloadBulkTemplate,
													onStarted: () => gridEl?.api.deselectAll(),
												}),
											"data-qualifier": "UniverseList/DropdownItem(BulkSphereTemplate)",
										} satisfies BulkActionDropdownItem,
									]
								: []),
						],
					},
					{
						label: "Share",
						icon: "share",
						async onClickAsync() {
							runWithErrorReporting(
								async () => {
									await spawnBulkShareAction({
										reasonKeys: Object.values(UniverseActionUnavailableReason),
										validator: universeListActionValidators.share,
										entities: selectedNodes,
										reasonToMessage: universeReasonToMessageMap,
										labels: { entitySingular: "Universe", entityPlural: "Universes" },
										getRedirectUrl: () => "",
										hideRedirectUrl: true,
										showEntityNames: true,
										area: "universe",
										customerAndLinkedAclProvider: (available) => () =>
											parallelize(
												available.map(
													(universe) => () =>
														universe.universeType === "GROUP_UNIVERSE"
															? universeWithGroupsRepository.getACL.fetchQuery(universe.uuid!)
															: universeRepository.getACL.fetchQuery(universe.uuid!),
												),
											),
										getEntityMetadata: (universe) => ({
											uuid: universe.uuid!,
											name: universe.name!,
											entityType: universe.universeType === "GROUP_UNIVERSE" ? "GROUP_UNIVERSE" : "UNIVERSE",
										}),
										async saveAclHandler(available, { aclByEntity }) {
											if (available.length > 0) {
												const universes = available.map(
													(universe): BulkAclEntity => ({
														accessControlList: aclByEntity[universe.uuid!],
														entityUuid: universe.uuid!,
													}),
												);
												await universeRepository.bulkShare(universes);
											}
										},
									});
								},
								{ area: "alerts", attemptedOperation: { message: "Bulk action entities error" } },
							);
						},
						"data-qualifier": "UniverseList/BatchAction(Share)",
					},
				]}
			/>
		</>
	);
};

export default UniverseList;
